import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import Bridge from "../../utils/Bridge";
import { Spin } from "antd";

const POLL_INTERVAL_MS = 2000;
const TOTAL_ITEMS = 8;
const DOT_TOTAL_TRIALS = 4;
const CUBE_TOTAL_TRIALS = 4;
const NUMBER_LOCATION_TOTAL_TRIALS = 4;
const SOCIAL_A_TOTAL_TRIALS = 6;
const SOCIAL_B_TOTAL_TRIALS = 6;

// Picture config matching research portal Language Naming (Form A)
// Images: scorpion, bow, helicopter, fox, axe, squirrel, swan, accordion
const PICTURE_CONFIG = [
  { id: 1, image: "scorpion.png", alt: "scorpion" },
  { id: 2, image: "coller.png", alt: "bow" },
  { id: 3, image: "helicopter.png", alt: "helicopter" },
  { id: 4, image: "fox.png", alt: "fox" },
  { id: 5, image: "rod.png", alt: "axe" },
  { id: 6, image: "rabbit.png", alt: "squirrel" },
  { id: 7, image: "duck.png", alt: "swan" },
  { id: 8, image: "music-tool.png", alt: "accordion" },
];

// Static comprehension grid order (top row, then bottom row):
// accordion, axe, bow, fox, helicopter, scorpion, squirrel, swan
const COMPREHENSION_PICTURES = [
  { id: 8, image: "music-tool.png", alt: "accordion" },
  { id: 5, image: "rod.png", alt: "axe" },
  { id: 2, image: "coller.png", alt: "bow" },
  { id: 4, image: "fox.png", alt: "fox" },
  { id: 3, image: "helicopter.png", alt: "helicopter" },
  { id: 1, image: "scorpion.png", alt: "scorpion" },
  { id: 6, image: "rabbit.png", alt: "squirrel" },
  { id: 7, image: "duck.png", alt: "swan" },
];

const getImageSrc = (filename) => {
  const base = process.env.PUBLIC_URL || "";
  return `${base}/assets/images/ecas/form-a/${filename}`;
};

const getDotsImageSrc = (filename) => {
  const base = process.env.PUBLIC_URL || "";
  return `${base}/assets/images/ecas/dots/${filename}`;
};

const NewECASLiveSession = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentTrial, setCurrentTrial] = useState(0);
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const [error, setError] = useState(null);
  const [section, setSection] = useState("naming"); // naming | comprehension | immediate_recall | spelling | letter_s | reverse_numbers | number_switching | letter_t | dots | cubes | number_location | sentence_completion | social_cognition_a | social_cognition_b | delayed_recall | delayed_recognition
  const [dotTrial, setDotTrial] = useState(0);
  const [cubeTrial, setCubeTrial] = useState(0);
  const [numberLocationTrial, setNumberLocationTrial] = useState(0);
  const [socialCognitionATrial, setSocialCognitionATrial] = useState(0);
  const [socialCognitionBTrial, setSocialCognitionBTrial] = useState(0);

  const fetchSession = useCallback(() => {
    const eals_id = localStorage.getItem("ap_eals_id");
    if (!eals_id) {
      setError("Session not found. Please log in again.");
      setLoading(false);
      return;
    }
    Bridge.personal_data_viewer.ecas_study
      .get_new_ecas_live_session(`?eals_id=${eals_id}`)
      .then((res) => {
        if (res.status === 1 && res.data) {
          setCurrentTrial(
            Math.min(
              7,
              Math.max(0, res.data.language_naming_current_trial || 0),
            ),
          );
          setDotTrial(
            Math.min(
              DOT_TOTAL_TRIALS - 1,
              Math.max(0, res.data.dot_counting_current_trial || 0),
            ),
          );
          setCubeTrial(
            Math.min(
              CUBE_TOTAL_TRIALS - 1,
              Math.max(0, res.data.cube_counting_current_trial || 0),
            ),
          );
          setNumberLocationTrial(
            Math.min(
              NUMBER_LOCATION_TOTAL_TRIALS - 1,
              Math.max(0, res.data.number_location_current_trial || 0),
            ),
          );
          if (typeof res.data.social_cognition_a_current_trial === "number") {
            setSocialCognitionATrial(
              Math.min(
                SOCIAL_A_TOTAL_TRIALS - 1,
                Math.max(0, res.data.social_cognition_a_current_trial || 0),
              ),
            );
          }
          if (typeof res.data.social_cognition_b_current_trial === "number") {
            setSocialCognitionBTrial(
              Math.min(
                SOCIAL_B_TOTAL_TRIALS - 1,
                Math.max(0, res.data.social_cognition_b_current_trial || 0),
              ),
            );
          }
          setHasActiveSession(res.data.hasActiveSession || false);
        }
        setError(null);
      })
      .catch((err) => {
        setError("Unable to load session.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchSession();
    const interval = setInterval(fetchSession, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchSession]);

  const currentPicture = PICTURE_CONFIG[currentTrial] || PICTURE_CONFIG[0];
  const progressCurrent = Math.min(currentTrial + 1, TOTAL_ITEMS);
  const isNaming = section === "naming";
  const isComprehension = section === "comprehension";
  const isImmediateRecall = section === "immediate_recall";
  const isSpelling = section === "spelling";
  const isLetterS = section === "letter_s";
  const isReverseNumbers = section === "reverse_numbers";
  const isNumberSwitching = section === "number_switching";
  const isLetterT = section === "letter_t";
  const isDots = section === "dots";
  const isCubes = section === "cubes";
  const isNumberLocation = section === "number_location";
  const isSentenceCompletion = section === "sentence_completion";
  const isSocialCognitionA = section === "social_cognition_a";
  const isSocialCognitionB = section === "social_cognition_b";
  const isDelayedRecall = section === "delayed_recall";
  const isDelayedRecognition = section === "delayed_recognition";
  const PROGRESS_BAR_FILL = "#2563eb";
  const showMediaBox =
    isNaming ||
    isComprehension ||
    isDots ||
    isCubes ||
    isNumberLocation ||
    isSocialCognitionA ||
    isSocialCognitionB;

  if (loading) {
    return (
      <div>
        <Header />
        <div className="main-content demographics-wrapper">
          <div className="container" style={{ minHeight: 300 }}>
            <div className="text-center py-5">
              <Spin size="large" />
              <p className="mt-3">Loading session...</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Header />
        <div className="main-content demographics-wrapper">
          <div className="container" style={{ minHeight: 300 }}>
            <div className="text-center py-5">
              <p className="text-danger">{error}</p>
              <button
                className="btn btn-primary mt-3"
                onClick={() => navigate("/my-personal-data-viewer")}
              >
                Back to My Data
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="main-content demographics-wrapper">
        <div className="container" style={{ minHeight: 300 }}>
          <a
            href="/my-personal-data-viewer"
            className="dashboard-back-arrow mt-0"
          >
            <box-icon
              name="chevron-left"
              color="#fff"
              style={{ position: "relative", top: 1 }}
            />
            Back to My Data
          </a>

          <div
            style={{
              background: "#fff",
              padding: "15px 20px",
              marginTop: 20,
              borderRadius: 8,
            }}
          >
            <h2
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: "#1f2937",
                marginBottom: 24,
              }}
            >
              Edinburgh Cognitive and Behavioural ALS Screen (ECAS)
            </h2>

            <div
              style={{
                background: "#f9fafb",
                borderRadius: 12,
                padding: "20px 24px",
                border: "1px solid #e5e7eb",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <span
                  style={{
                    fontSize: 20,
                    fontWeight: 600,
                    color: "#000",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {isNaming
                    ? "1. LANGUAGE - NAMING"
                    : isComprehension
                    ? "2. LANGUAGE - COMPREHENSION"
                    : isImmediateRecall
                    ? "3. MEMORY - IMMEDIATE RECALL"
                    : isSpelling
                    ? "4. LANGUAGE - SPELLING"
                    : isLetterS
                    ? "5. VERBAL FLUENCY - LETTER S"
                    : isReverseNumbers
                    ? "6. REVERSE THE SPOKEN NUMBERS"
                    : isNumberSwitching
                    ? "7: NUMBER/LETTER SWITCHING"
                    : isLetterT
                    ? "8: VERBAL FLUENCY - LETTER T"
                    : isDots
                    ? "9: COUNT DOTS IN EACH BOX"
                    : isCubes
                    ? "10: COUNT CUBES IN EACH BOX"
                    : isNumberLocation
                    ? "11: WHICH NUMBER HAS THE DOT?"
                    : isSentenceCompletion
                    ? "12: SENTENCE COMPLETION"
                    : isSocialCognitionA
                    ? "13 (PART A): SOCIAL COGNITION"
                    : isSocialCognitionB
                    ? "13 (PART B): SOCIAL COGNITION"
                    : isDelayedRecall
                    ? "14. MEMORY - DELAYED RECALL"
                    : "15. MEMORY - DELAYED RECOGNITION"}
                </span>
              </div>

              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: "#1f2937",
                  marginBottom:
                    isImmediateRecall ||
                    isSpelling ||
                    isLetterS ||
                    isReverseNumbers ||
                    isNumberSwitching ||
                    isLetterT ||
                    isDots
                      ? 6
                      : 12,
                }}
              >
                {isNaming ? (
                  ""
                ) : isComprehension ? (
                  "Which picture matches?"
                ) : isImmediateRecall ? (
                  "Immediate recall after story"
                ) : isSpelling ? (
                  ""
                ) : isLetterS ? (
                  ""
                ) : isReverseNumbers ? (
                  ""
                ) : isNumberSwitching ? (
                  ""
                ) : isLetterT ? (
                  ""
                ) : isDots ? (
                  ""
                ) : isCubes ? (
                  ""
                ) : isNumberLocation ? (
                  ""
                ) : isSentenceCompletion ? (
                  ""
                ) : isSocialCognitionA ? (
                  ""
                ) : isSocialCognitionB ? (
                  ""
                ) : isDelayedRecall ? (
                  ""
                ) : isDelayedRecognition ? (
                  ""
                ) : null}
              </div>

              {(isImmediateRecall ||
                isSpelling ||
                isLetterS ||
                isReverseNumbers ||
                isNumberSwitching ||
                isLetterT ||
                isDots ||
                isCubes ||
                isNumberLocation ||
                isSentenceCompletion ||
                isSocialCognitionA ||
                isSocialCognitionB ||
                isDelayedRecall ||
                isDelayedRecognition) && (
                <div
                  style={{
                    fontSize: 17,
                    color: "#374151",
                    marginBottom: 16,
                    lineHeight: 1.5,
                  }}
                >
                  {isImmediateRecall
                    ? "Instructions: Listen carefully to the story that's going to be presented to you and recall everything you remember after."
                    : isSpelling
                    ? "Instructions: You will be given different words - please spell them aloud to the best of your ability."
                    : isLetterS
                    ? "Instructions: You will be given different words - list as many words that start with an S as possible."
                    : isReverseNumbers
                    ? "Instructions: Numbers will be read aloud to you. Repeat them in reverse order."
                    : isLetterT
                    ? "Instructions: List as many words that start with an T and are four letters as possible."
                    : isDots
                    ? "Instructions: For each box, count the number of dots you see."
                    : isCubes
                    ? "Instructions: For each picture, count the number of cubes you see."
                    : isNumberLocation
                    ? "Instructions: Look carefully and choose which number has the dot."
                    : isSentenceCompletion
                    ? "You will be given a partial sentence, please complete it with a word that doesn't make sense."
                    : isSocialCognitionA
                    ? "Please choose which image you like best."
                    : isDelayedRecall
                    ? "Recall the story again from earlier and state everything you can remember."
                    : isDelayedRecognition
                    ? "Please answer the following True or False questions about the story."
                    : "Instructions: Alternating between numbers and letters, in order, without skipping any until told to stop."}
                </div>
              )}

              {(isNaming || isSocialCognitionA || isSocialCognitionB) && (
                <div style={{ marginBottom: 12 }}>
                  <span
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: "#111827",
                    }}
                  >
                    {isNaming
                      ? `Image ${progressCurrent} of ${TOTAL_ITEMS}`
                      : isSocialCognitionA
                      ? `Image ${
                          (typeof socialCognitionATrial === "number"
                            ? socialCognitionATrial
                            : 0) + 1
                        } of ${SOCIAL_A_TOTAL_TRIALS}`
                      : `Image ${
                          (typeof socialCognitionBTrial === "number"
                            ? socialCognitionBTrial
                            : 0) + 1
                        } of ${SOCIAL_B_TOTAL_TRIALS}`}
                  </span>
                  <div
                    style={{
                      height: 6,
                      background: "#e5e7eb",
                      borderRadius: 3,
                      marginTop: 6,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${
                          isNaming
                            ? (progressCurrent / TOTAL_ITEMS) * 100
                            : isSocialCognitionA
                            ? (((typeof socialCognitionATrial === "number"
                                ? socialCognitionATrial
                                : 0) +
                                1) /
                                SOCIAL_A_TOTAL_TRIALS) *
                              100
                            : (((typeof socialCognitionBTrial === "number"
                                ? socialCognitionBTrial
                                : 0) +
                                1) /
                                SOCIAL_B_TOTAL_TRIALS) *
                              100
                        }%`,
                        height: "100%",
                        background: PROGRESS_BAR_FILL,
                        borderRadius: 3,
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                </div>
              )}

              {(isDots || isCubes || isNumberLocation) && (
                <div style={{ marginBottom: 12 }}>
                  <span
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: "#111827",
                    }}
                  >
                    {isDots
                      ? `Box ${Math.min(dotTrial + 1, DOT_TOTAL_TRIALS)} of ${DOT_TOTAL_TRIALS}`
                      : isCubes
                      ? `Box ${Math.min(cubeTrial + 1, CUBE_TOTAL_TRIALS)} of ${CUBE_TOTAL_TRIALS}`
                      : `Box ${
                          Math.min(
                            numberLocationTrial + 1,
                            NUMBER_LOCATION_TOTAL_TRIALS,
                          )
                        } of ${NUMBER_LOCATION_TOTAL_TRIALS}`}
                  </span>
                  <div
                    style={{
                      height: 6,
                      background: "#e5e7eb",
                      borderRadius: 3,
                      marginTop: 6,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${
                          isDots
                            ? (Math.min(dotTrial + 1, DOT_TOTAL_TRIALS) /
                                DOT_TOTAL_TRIALS) *
                              100
                            : isCubes
                            ? (Math.min(cubeTrial + 1, CUBE_TOTAL_TRIALS) /
                                CUBE_TOTAL_TRIALS) *
                              100
                            : (Math.min(
                                numberLocationTrial + 1,
                                NUMBER_LOCATION_TOTAL_TRIALS,
                              ) /
                                NUMBER_LOCATION_TOTAL_TRIALS) *
                              100
                        }%`,
                        height: "100%",
                        background: PROGRESS_BAR_FILL,
                        borderRadius: 3,
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                </div>
              )}

              {showMediaBox && (
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 12,
                    padding: 36,
                    minHeight: 340,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  {isNaming ? (
                    <>
                      <img
                        src={getImageSrc(currentPicture.image)}
                        alt={currentPicture.alt}
                        style={{
                          maxWidth: "100%",
                          maxHeight: 300,
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling?.classList.remove("d-none");
                        }}
                      />
                      <div className="d-none" style={{ textAlign: "center" }}>
                        <span style={{ color: "#6b7280" }}>
                          Image {currentPicture.id}
                        </span>
                      </div>
                    </>
                  ) : isComprehension ? (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                        gap: 20,
                        width: "100%",
                      }}
                    >
                      {COMPREHENSION_PICTURES.map((pic) => (
                        <div
                          key={pic.id}
                          style={{
                            background: "#f9fafb",
                            borderRadius: 12,
                            padding: 16,
                            border: "1px solid #e5e7eb",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: 160,
                          }}
                        >
                          <img
                            src={getImageSrc(pic.image)}
                            alt={pic.alt}
                            style={{
                              maxWidth: "100%",
                              maxHeight: 140,
                              objectFit: "contain",
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  ) : isDots ? (
                    <div
                      style={{
                        width: "100%",
                        maxWidth: 640,
                        margin: "0 auto",
                        background: "#f9fafb",
                        borderRadius: 12,
                        padding: 24,
                        border: "1px solid #e5e7eb",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={getDotsImageSrc(`${dotTrial + 1}.png`)}
                        alt={`Dot counting box ${dotTrial + 1}`}
                        style={{
                          maxWidth: "100%",
                          maxHeight: 240,
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  ) : isCubes ? (
                    <div
                      style={{
                        width: "100%",
                        maxWidth: 640,
                        margin: "0 auto",
                        background: "#f9fafb",
                        borderRadius: 12,
                        padding: 24,
                        border: "1px solid #e5e7eb",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={`${
                          process.env.PUBLIC_URL || ""
                        }/assets/images/ecas/cubes/${cubeTrial + 1}.png`}
                        alt={`Cube counting structure ${cubeTrial + 1}`}
                        style={{
                          maxWidth: "100%",
                          maxHeight: 240,
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  ) : isNumberLocation ? (
                    <div
                      style={{
                        width: "100%",
                        maxWidth: 640,
                        margin: "0 auto",
                        background: "#f9fafb",
                        borderRadius: 12,
                        padding: 24,
                        border: "1px solid #e5e7eb",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        gap: 16,
                      }}
                    >
                      <img
                        src={`${
                          process.env.PUBLIC_URL || ""
                        }/assets/images/ecas/number-location/${
                          numberLocationTrial + 1
                        }.png`}
                        alt={`Number location card ${numberLocationTrial + 1}`}
                        style={{
                          maxWidth: "100%",
                          maxHeight: 260,
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  ) : isSocialCognitionA ? (
                    <div
                      style={{
                        width: "100%",
                        maxWidth: 640,
                        margin: "0 auto",
                        background: "#f9fafb",
                        borderRadius: 12,
                        padding: 24,
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                          gap: 16,
                        }}
                      >
                        {[1, 2, 3, 4].map((optIndex) => (
                          <div
                            key={optIndex}
                            style={{
                              background: "#ffffff",
                              borderRadius: 12,
                              padding: 12,
                              border: "1px solid #e5e7eb",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minHeight: 110,
                            }}
                          >
                            <img
                              src={`${
                                process.env.PUBLIC_URL || ""
                              }/assets/images/ecas/social-cognition-part-a/${
                                (typeof socialCognitionATrial === "number"
                                  ? socialCognitionATrial
                                  : 0) + 1
                              }/${optIndex}.png`}
                              alt={`Social cognition choice ${optIndex}`}
                              style={{
                                maxWidth: "100%",
                                maxHeight: 90,
                                objectFit: "contain",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : isSocialCognitionB ? (
                    <div
                      style={{
                        width: "100%",
                        maxWidth: 640,
                        margin: "0 auto",
                        background: "#f9fafb",
                        borderRadius: 12,
                        padding: 24,
                        border: "1px solid #e5e7eb",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={`${
                          process.env.PUBLIC_URL || ""
                        }/assets/images/ecas/social-cognition-part-b/${
                          (typeof socialCognitionBTrial === "number"
                            ? socialCognitionBTrial
                            : 0) + 1
                        }.png`}
                        alt={`Social cognition Part B card ${
                          (typeof socialCognitionBTrial === "number"
                            ? socialCognitionBTrial
                            : 0) + 1
                        }`}
                        style={{
                          maxWidth: "100%",
                          maxHeight: 260,
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 24,
                }}
              >
                <button
                  type="button"
                  disabled={section === "naming"}
                  onClick={() => {
                    if (section === "comprehension") {
                      setSection("naming");
                    } else if (section === "immediate_recall") {
                      setSection("comprehension");
                    } else if (section === "spelling") {
                      setSection("immediate_recall");
                    } else if (section === "letter_s") {
                      setSection("spelling");
                    } else if (section === "reverse_numbers") {
                      setSection("letter_s");
                    } else if (section === "number_switching") {
                      setSection("reverse_numbers");
                    } else if (section === "letter_t") {
                      setSection("number_switching");
                    } else if (section === "dots") {
                      setSection("letter_t");
                    } else if (section === "cubes") {
                      setSection("dots");
                    } else if (section === "number_location") {
                      setSection("cubes");
                    } else if (section === "sentence_completion") {
                      setSection("number_location");
                    } else if (section === "social_cognition_a") {
                      setSection("sentence_completion");
                    } else if (section === "social_cognition_b") {
                      setSection("social_cognition_a");
                    } else if (section === "delayed_recall") {
                      setSection("social_cognition_b");
                    } else if (section === "delayed_recognition") {
                      setSection("delayed_recall");
                    } else if (section !== "naming") {
                      navigate("/my-personal-data-viewer");
                    }
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "12px 14px",
                    borderRadius: 999,
                    border:
                      section === "naming"
                        ? "1px solid #d1d5db"
                        : "1px solid rgba(255,255,255,0.18)",
                    background: section === "naming" ? "#9ca3af" : "#d06338",
                    color: "#fff",
                    fontWeight: 600,
                    letterSpacing: "0.2px",
                    boxShadow:
                      section === "naming"
                        ? "none"
                        : "0 10px 24px rgba(208, 99, 56, 0.35)",
                    cursor: section === "naming" ? "not-allowed" : "pointer",
                  }}
                  aria-label="Previous"
                >
                  <box-icon name="chevron-left" color="#fff" />
                  Prev
                </button>
                <button
                  type="button"
                  disabled={section === "delayed_recognition"}
                  onClick={() => {
                    if (section === "naming") {
                      setSection("comprehension");
                    } else if (section === "comprehension") {
                      setSection("immediate_recall");
                    } else if (section === "immediate_recall") {
                      setSection("spelling");
                    } else if (section === "spelling") {
                      setSection("letter_s");
                    } else if (section === "letter_s") {
                      setSection("reverse_numbers");
                    } else if (section === "reverse_numbers") {
                      setSection("number_switching");
                    } else if (section === "number_switching") {
                      setSection("letter_t");
                    } else if (section === "letter_t") {
                      setSection("dots");
                    } else if (section === "dots") {
                      setSection("cubes");
                    } else if (section === "cubes") {
                      setSection("number_location");
                    } else if (section === "number_location") {
                      setSection("sentence_completion");
                    } else if (section === "sentence_completion") {
                      setSection("social_cognition_a");
                    } else if (section === "social_cognition_a") {
                      setSection("social_cognition_b");
                    } else if (section === "social_cognition_b") {
                      setSection("delayed_recall");
                    } else if (section === "delayed_recall") {
                      setSection("delayed_recognition");
                    } else if (section !== "delayed_recognition") {
                      navigate("/my-personal-data-viewer");
                    }
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "12px 14px",
                    borderRadius: 999,
                    border:
                      section === "delayed_recognition"
                        ? "1px solid #d1d5db"
                        : "1px solid rgba(255,255,255,0.18)",
                    background:
                      section === "delayed_recognition" ? "#9ca3af" : "#d06338",
                    color: "#fff",
                    fontWeight: 600,
                    letterSpacing: "0.2px",
                    boxShadow:
                      section === "delayed_recognition"
                        ? "none"
                        : "0 10px 24px rgba(208, 99, 56, 0.35)",
                    cursor:
                      section === "delayed_recognition"
                        ? "not-allowed"
                        : "pointer",
                  }}
                  aria-label="Next"
                >
                  Next
                  <box-icon name="chevron-right" color="#fff" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default NewECASLiveSession;
